import React from "react";
import { useLang } from "../context/LangContext";
import PageHeader from "../components/site/PageHeader";
import { LOCAL_PARTNERS, IMG } from "../lib/siteData";
import { MapPin, ArrowRight, ArrowUpRight, Mail, Info, Globe } from "lucide-react";
import { Link } from "react-router-dom";

const Partners = () => {
  const { t } = useLang();
  return (
    <div data-testid="partners-page">
      <PageHeader
        eyebrow={t("nav.partners")}
        title="Strategic Local Partners"
        subtitle="Waves Technology FZCO works with selected local partners across the Middle East to support project development, business coordination and on-the-ground execution."
        image={IMG.waterTreatment}
      />

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-steel-200 border border-steel-200">
          {LOCAL_PARTNERS.map((p) => (
            <article key={p.key} data-testid={`partner-${p.key}`} className="bg-white p-8 lg:p-10 indicator-line flex flex-col">
              <div className="h-32 lg:h-40 bg-steel-50 border border-steel-200 flex items-center justify-center p-6 mb-6">
                <img
                  src={p.logo}
                  alt={`${p.name} logo`}
                  className="max-h-full max-w-full object-contain"
                  data-testid={`partner-logo-${p.key}`}
                />
              </div>
              <div className="overline mb-2">{p.role}</div>
              <h3 className="font-heading font-extrabold text-2xl lg:text-3xl text-ink leading-tight mb-3">{p.name}</h3>
              <div className="flex items-center gap-2 text-xs text-brand font-semibold uppercase tracking-[0.2em] mb-4">
                <MapPin className="w-3.5 h-3.5" /> {p.region}
              </div>
              <p className="text-sm lg:text-base text-ink-muted leading-relaxed mb-6">{p.description}</p>

              <div className="mt-auto pt-4 border-t border-steel-200 space-y-2 text-sm">
                {p.website && (
                  <a
                    href={p.website}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-2 text-ink-soft hover:text-brand"
                    data-testid={`partner-website-${p.key}`}
                  >
                    <Globe className="w-4 h-4 text-brand" strokeWidth={1.6} />
                    <span className="font-mono">{p.websiteLabel || p.website}</span>
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                )}
                {p.email && (
                  <a
                    href={`mailto:${p.email}`}
                    className="inline-flex items-center gap-2 text-ink-soft hover:text-brand"
                    data-testid={`partner-email-${p.key}`}
                  >
                    <Mail className="w-4 h-4 text-brand" strokeWidth={1.6} />
                    <span className="font-mono">{p.email}</span>
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="py-12 bg-steel-50 border-y border-steel-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="bg-white border-l-4 border-brand p-6 lg:p-8 flex items-start gap-4">
            <Info className="w-6 h-6 text-brand shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <div className="overline mb-2">Partnership Note</div>
              <p className="text-sm text-ink-muted leading-relaxed">
                Additional supplier and technology relationships are managed on a project-by-project basis. Manufacturer authorizations, approval letters and partnership scope can be shared on request, subject to commercial confidentiality.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <h3 className="font-heading font-bold text-2xl lg:text-3xl max-w-2xl text-ink">
            Looking for a specific technology or qualified vendor for your project?
          </h3>
          <Link to="/rfq" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-7 py-4 font-semibold text-sm uppercase tracking-[0.18em] transition-colors" data-testid="partners-rfq-cta">
            {t("common.submitRfq")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Partners;
